import { exists, UnreachableError } from 'base/helpers';
import { CompiledScheme } from 'base/styleguide/types';
import { shouldStyleBeAlternate } from 'base/styleguide/ui_styles';
import { TempAsset } from 'base/webflow/asset';
import {
  BlocksDataAttribute,
  extractTreeByName,
  getDataAttr,
  getNodeName,
  removeBlocksDataAttributes,
} from 'base/webflow/data_attributes';
import { fromNested } from 'base/webflow/nested';
import {
  mergeAssets,
  mergeIX2,
  prefixComponentNodeIds,
} from 'base/webflow/page_component';
import { pQuery } from 'base/webflow/pQuery';
import {
  isReplaceableTextNode,
  TextReplacementType,
} from 'base/webflow/replaceable_content';
import { Component, IX2, NestedNode } from 'base/webflow/schema';
import { styleGuideStyleById } from 'base/webflow/style_guide';
import {
  applyClassIdUpdates,
  componentStyleTypeclass,
  mergeStyles,
  resolveComboClass,
} from 'base/webflow/style_util';
import { ElementInstance, ElementInstanceState } from 'blocks/editor/model';

/** Exports an ElementInstance tree to a webflow component */
export function exportElementToWebflow(
  rootInstance: ElementInstance | ElementInstanceState,
  getComponent: (slug: string) => Component | undefined,
  opts: {
    scheme?: CompiledScheme;
    schemeNumber?: number;
    assetByImageKey?: Map<string, TempAsset>;
  } = {},
): Component {
  let idPrefixCounter = 0;

  const ix2s: IX2[] = [];
  const assets: Component['payload']['assets'][] = [];
  let styles: Component['payload']['styles'] = [];
  let loadedComponents = new Map<string, Component>();

  /** Avoid merging styles from the same component multiple times. */
  function loadComponentAndMergeStyles(
    componentSlug: string,
  ): Component | undefined {
    const cached = loadedComponents.get(componentSlug);
    if (cached) return cached;
    const rawComponent = getComponent(componentSlug);
    if (!rawComponent) return undefined;
    const mergeRes = mergeStyles(
      styles,
      rawComponent.payload.styles,
      componentStyleTypeclass,
    );
    styles = mergeRes.styles;
    assets.push(rawComponent.payload.assets);
    loadedComponents.set(componentSlug, rawComponent);
    return applyClassIdUpdates(mergeRes.idUpdates, rawComponent);
  }

  function reconstruct(
    instance: ElementInstance | ElementInstanceState,
  ): NestedNode {
    // Step 1: Construct the root of the component instance.
    //
    // We apply a ID prefix to all nodes, so that if we have multiple instances
    // of the same componentSlug they don't collide.
    // (This means also duplicating all the ix2s and assets.  Yolo 📈)
    const idPrefix = `s${idPrefixCounter++}_`;
    const rawComponent = loadComponentAndMergeStyles(instance.source.slug);
    if (rawComponent == null) {
      return {
        _id: idPrefix + instance.source.rootName,
        text: true,
        v: `ElememtSource not loaded: ${instance.source.slug}`,
        children: [],
      };
    }
    const component = prefixComponentNodeIds(idPrefix, rawComponent);
    ix2s.push(component.payload.ix2);
    let root = applyTextChanges(
      extractTreeByName(component, instance.source.rootName),
      (nodeId) => instance.textOverrides.get(nodeId.slice(idPrefix.length)),
    );

    if (opts.assetByImageKey != null && opts.assetByImageKey.size > 0) {
      root = applyImageOverrides({
        scheme: opts.scheme,
        node: root,
        assetByImageKey: opts.assetByImageKey,
        getImageKey: (nodeId) =>
          `${instance.id}:${nodeId.slice(idPrefix.length)}`,
      });
    }

    const nodesByName = createNodesByName(root);

    // Step 2: Find the slots inside the new root & reconstruct them.
    for (const [
      slotName,
      { items: items },
    ] of instance.slotOverrides.entries()) {
      const slotNode = nodesByName.get(slotName);
      if (slotNode == null) {
        throw new Error(
          `Slot ${slotName} not found in ${instance.source.slug}/${instance.source.rootName}`,
        );
      }
      slotNode.children = items.map((e) => reconstruct(e));
    }

    // Step 3: Apply the element.margin property
    if (!instance.margin) {
      return root;
    }
    return {
      _id: root._id + '-rootmargin',
      data: {},
      tag: 'div',
      classes: resolveComboClass(
        [
          `margin-${instance.margin.direction}`,
          `margin-${instance.margin.size}`,
        ],
        styleGuideStyleById,
      ),
      children: [root],
    };
  }

  const newRootNode = reconstruct(rootInstance);
  const rawRootComponent = getComponent(rootInstance.source.slug);
  let component: Component = rawRootComponent
    ? {
        // Webflow crashes if we don't include a "meta" field.
        // Let's just use the "meta" from the first component.
        ...rawRootComponent,
        payload: {
          ix1: [],
          ix2: mergeIX2(ix2s),
          assets: mergeAssets(assets),
          nodes: fromNested([removeEmptySlotNodes(newRootNode)]),
          styles,
        },
      }
    : emptyComponent;

  let $payload = pQuery(component);

  if (rootInstance.source.rootName == null) {
    const $root = $payload.getRootNode();
    if ($root != null) {
      // The way we are applying the color scheme is by adding a utility class to
      // the section, it also includes css glue to override the scheme variables
      // so that nested elements will use associated color scheme.
      //
      // Due to how Webflow style inheritance works, because we are adding a
      // utility class style to the element, we need to remove properties that
      // are overridden (text and background).
      for (const $style of $root.styles()) {
        const updatedRootProperties = $style.styleLess
          .split(';')
          .map((property) => {
            const [_, value] = property.split(':').map((s) => s.trim());
            if (
              value === '@var_relume-variable--color-scheme-1-background' ||
              value === '@var_relume-variable--color-scheme-1-text'
            ) {
              return null;
            }

            return property;
          })
          .filter(exists);

        $style.styleLess = `${updatedRootProperties.join('; ')};`;
      }

      if (opts.schemeNumber != null) {
        $root.addStyle({
          name: `color-scheme-${opts.schemeNumber}`,
          styleLess: '',
        });
      }

      if (opts.scheme != null) {
        for (const $node of $root.walkForwardAll()) {
          const classNames = $node.styles().map((s) => s.name);
          const isAlternate = shouldStyleBeAlternate(opts.scheme, classNames);
          const alreadyHasIsAlternate = classNames.includes('is-alternate');

          if (isAlternate && !alreadyHasIsAlternate) {
            $node.addStyle({
              name: 'is-alternate',
              styleLess: '',
            });
          } else if (!isAlternate && alreadyHasIsAlternate) {
            if (classNames[classNames.length - 1] === 'is-alternate') {
              $node.removeLastStyle();
            } else {
              console.warn("Can't remove is-alternate from", classNames);
            }
          }
        }
      }
    }
  }
  component = removeBlocksDataAttributes($payload.ref);
  return component;
}

const emptyComponent: Component = {
  type: '@webflow/XscpData',
  payload: {
    ix1: [],
    ix2: {
      interactions: [],
      actionLists: [],
      events: [],
    },
    assets: [],
    nodes: [],
    styles: [],
  },
};

function removeEmptySlotNodes(node: NestedNode): NestedNode {
  if (node.text) return node;
  const children = node.children.flatMap((child) => {
    if (onlyEmptySlotNodeOrWrapper(child)) {
      return [];
    }
    return [removeEmptySlotNodes(child)];
  });
  return { ...node, children };
}

function onlyEmptySlotNodeOrWrapper(node: NestedNode): boolean {
  if (node.text) return false;
  return (
    // Is an empty slot node
    (node.children.length === 0 &&
      !!(
        getDataAttr(node, BlocksDataAttribute.CHILDREN_SLOT) ||
        getDataAttr(node, BlocksDataAttribute.SELF_SLOT)
      ) &&
      !!getNodeName(node)) ||
    // Is a wrapper of an empty slot node
    (node.children.length === 1 &&
      node.children.every(onlyEmptySlotNodeOrWrapper))
  );
}

function createNodesByName(root: NestedNode): Map<string, NestedNode> {
  const res = new Map<string, NestedNode>();
  function walk(node: NestedNode) {
    const name = getNodeName(node);
    if (name) res.set(name, node);
    node.children.forEach(walk);
  }
  walk(root);
  return res;
}

function applyImageOverrides({
  node,
  scheme,
  assetByImageKey,
  getImageKey,
}: {
  node: NestedNode;
  scheme?: CompiledScheme;
  assetByImageKey: Map<
    string,
    | { type: 'asset'; id: string; url: string }
    | { type: 'logo'; height: string }
  >;
  getImageKey: (nodeId: string) => string;
}): NestedNode {
  const imageKey = getImageKey(node._id);

  const children = node.children.map((child) => {
    return applyImageOverrides({
      node: child,
      scheme,
      assetByImageKey,
      getImageKey,
    });
  });

  if (node.text || node.type !== 'Image') return { ...node, children };

  let height = 'auto';

  let imageOverride = assetByImageKey?.get(imageKey);
  if (imageOverride?.type === 'logo') {
    const preferDarkLogo = scheme?.alternate.has('logo') ?? false;
    height = imageOverride.height;
    imageOverride = preferDarkLogo
      ? assetByImageKey.get('logoDark')
      : assetByImageKey.get('logoLight');
  }

  if (imageOverride == null || imageOverride.type === 'logo') {
    return { ...node, children };
  }

  return {
    ...node,
    children,
    data: {
      ...node.data,
      img: {
        ...node.data.img,
        id: imageOverride.id,
      },
      attr: {
        ...node.data.attr,
        width: 'auto',
        height,
        alt: '__wf_reserved_inherit',
        loading: 'eager',
        src: imageOverride.url,
      },
    },
  };
}

function applyTextChanges(
  node: NestedNode,
  getOverride: (id: string) => string | undefined,
): NestedNode {
  if (node.text) return node;
  const newValue = getOverride(node._id);
  const children = node.children.map((child) =>
    applyTextChanges(child, getOverride),
  );
  if (newValue == null) return { ...node, children };

  if (node.type === 'RichText') {
    const paragraphs = newValue.trim().split('\n');
    const children = paragraphs.map<NestedNode>((v, i) => ({
      _id: `${node._id}-p-${i}`,
      classes: [],
      type: 'Paragraph',
      tag: 'p',
      data: {},
      children: [
        {
          _id: `${node._id}-p-${i}-text`,
          text: true,
          v,
          children: [],
        },
      ],
    }));
    return { ...node, children };
  }

  const textReplacementType = isReplaceableTextNode(node);
  if (textReplacementType === TextReplacementType.CHILDREN) {
    let lines = newValue.trim().split('\n');
    if (lines.length === 0) lines = [''];
    return {
      ...node,
      children: lines.flatMap((v, i): NestedNode[] =>
        [
          i != 0
            ? {
                _id: `${node._id}-br-${i}`,
                classes: [],
                type: 'LineBreak',
                tag: 'br',
                data: {
                  sym: {
                    inst: 'LineBreak',
                  },
                },
                children: [],
              }
            : undefined,
          {
            _id: `${node._id}-text-${i}`,
            text: true as const,
            v,
            children: [],
          },
        ].filter(exists),
      ),
    };
  } else if (textReplacementType === TextReplacementType.VALUE) {
    return {
      ...node,
      children,
      data: {
        ...node.data,
        attr: { ...node.data.attr, value: newValue },
      },
    };
  } else if (textReplacementType === TextReplacementType.PLACEHOLDER) {
    return {
      ...node,
      children,
      data: {
        ...node.data,
        attr: { ...node.data.attr, placeholder: newValue },
      },
    };
  } else if (textReplacementType === TextReplacementType.SELECT_FIRST_OPTION) {
    const { form } = node.data;
    if (form?.type !== 'select') {
      throw new Error(`invalid text replacement form ${form?.type}`);
    }
    const { opts = [] } = form;
    return {
      ...node,
      children,
      data: {
        ...node.data,
        form: {
          ...form,
          type: 'select',
          opts: [{ ...opts[0], t: newValue }, ...opts.slice(1)],
        },
      },
    };
  } else if (textReplacementType == null) {
    return { ...node, children };
  } else {
    throw new UnreachableError(textReplacementType);
  }
}
